import React, { useEffect, useState } from 'react';
import {
  LuMailOpen,
  LuCircleUser,
  LuCalendarSearch,
  LuTicket,
  LuPartyPopper,
  LuMessageCircle,
  LuGripVertical,
} from 'react-icons/lu';

const JOURNEY = [
  {
    id: 'invitation',
    label: 'Receive an invitation',
    Icon: LuMailOpen,
  },
  {
    id: 'account',
    label: 'Create an account',
    Icon: LuCircleUser,
  },
  {
    id: 'browse',
    label: 'Browse experiences',
    Icon: LuCalendarSearch,
  },
  {
    id: 'reserve',
    label: 'Reserve a spot',
    Icon: LuTicket,
  },
  {
    id: 'attend',
    label: 'Show up & enjoy',
    Icon: LuPartyPopper,
  },
  {
    id: 'chat',
    label: 'Continue chatting',
    Icon: LuMessageCircle,
  },
];

const SHUFFLED_IDS = [
  'browse',
  'invitation',
  'chat',
  'account',
  'attend',
  'reserve',
];

const STEPS = [
  {
    type: 'lesson',
    narration:
      'Now, you might be wondering what all of this actually looks like for a member. First, they receive an invitation from the Tribe team or someone in the community. They use their invite code to create an account, browse upcoming experiences, and reserve a spot at one they want to attend. When they arrive, a trained Tribe host guides the experience and helps them make new connections. After the experience ends, they can continue chatting with the people they met through the app.',
  },
  {
    type: 'activity',
    requiresCompletion: true,
    prompt: 'Put the member journey in order.',
  },
];

const STAGE_TIMINGS = [
  4800,
  9200,
  12000,
  15000,
  19000,
  28500,
  34500,
];

export default function MemberJourney({
  part,
  audioStatus,
  playbackId,
  onComplete,
}) {
  const [visualStage, setVisualStage] = useState(0);
  const step = STEPS[part];

  useEffect(() => {
    if (
      part !== 0 ||
      audioStatus !== 'Playing'
    ) {
      return undefined;
    }

    setVisualStage(0);

    const timers = STAGE_TIMINGS.map(
      (time, index) =>
        window.setTimeout(
          () => setVisualStage(index + 1),
          time
        )
    );

    return () => {
      timers.forEach(window.clearTimeout);
    };
  }, [part, audioStatus, playbackId]);

  useEffect(() => {
    if (
      part === 0 &&
      audioStatus === 'Complete'
    ) {
      setVisualStage(STAGE_TIMINGS.length);
    }
  }, [part, audioStatus]);

  if (step.type === 'activity') {
    return (
      <JourneyOrderActivity
        prompt={step.prompt}
        onComplete={onComplete}
      />
    );
  }

  return (
    <section className="member-journey-slide">
      <style>{STYLES}</style>

      <JourneySequence stage={visualStage} />
    </section>
  );
}

function JourneySequence({ stage }) {
  const routeVisible = stage > 0;
  const reviewing = stage > JOURNEY.length;

  const activeIndex = routeVisible
    ? Math.min(
        stage - 1,
        JOURNEY.length - 1
      )
    : -1;

  const progress =
    activeIndex <= 0
      ? 0
      : activeIndex /
        (JOURNEY.length - 1);

  return (
    <div
      className={[
        'journey-route-scene',
        routeVisible ? 'is-active' : '',
        reviewing ? 'is-reviewing' : '',
      ].join(' ')}
    >
      <div className="journey-route-title">
        <WordText
          as="h1"
          text="How Tribe works for members"
          wordDelay={180}
        />
      </div>

      <div className="journey-route">
        <div
          className="journey-route-progress"
          style={{
            transform: `scaleX(${
              reviewing ? 1 : progress
            })`,
          }}
          aria-hidden="true"
        />

        <div
          className="journey-route-line"
          aria-hidden="true"
        />

        <div className="journey-route-nodes">
          {JOURNEY.map((item, index) => {
            const revealed =
              routeVisible &&
              index <= activeIndex;

            const active =
              !reviewing &&
              index === activeIndex;

            const Icon = item.Icon;

            return (
              <div
                className={[
                  'journey-route-node',
                  revealed
                    ? 'is-revealed'
                    : '',
                  active
                    ? 'is-current'
                    : '',
                ].join(' ')}
                key={item.id}
              >
                <div className="journey-route-icon">
                  <Icon aria-hidden="true" />
                </div>

                <span
                  className="journey-route-dot"
                  aria-hidden="true"
                />

                <strong>
                  {item.label}
                </strong>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function JourneyOrderActivity({
  prompt,
  onComplete,
}) {
  const [items, setItems] = useState(() =>
    SHUFFLED_IDS.map((id) =>
      JOURNEY.find(
        (item) => item.id === id
      )
    )
  );

  const [draggedId, setDraggedId] =
    useState('');

  const [selectedId, setSelectedId] =
    useState('');

  const [result, setResult] =
    useState('');

  function moveItem(
    sourceId,
    targetId
  ) {
    if (
      !sourceId ||
      !targetId ||
      sourceId === targetId
    ) {
      return;
    }

    setItems((currentItems) => {
      const nextItems = [...currentItems];

      const sourceIndex =
        nextItems.findIndex(
          (item) =>
            item.id === sourceId
        );

      const targetIndex =
        nextItems.findIndex(
          (item) =>
            item.id === targetId
        );

      if (
        sourceIndex < 0 ||
        targetIndex < 0
      ) {
        return currentItems;
      }

      const [movedItem] =
        nextItems.splice(
          sourceIndex,
          1
        );

      nextItems.splice(
        targetIndex,
        0,
        movedItem
      );

      return nextItems;
    });

    setResult('');
  }

  function swapItems(
    firstId,
    secondId
  ) {
    setItems((currentItems) => {
      const nextItems = [...currentItems];

      const firstIndex =
        nextItems.findIndex(
          (item) =>
            item.id === firstId
        );

      const secondIndex =
        nextItems.findIndex(
          (item) =>
            item.id === secondId
        );

      if (
        firstIndex < 0 ||
        secondIndex < 0
      ) {
        return currentItems;
      }

      [
        nextItems[firstIndex],
        nextItems[secondIndex],
      ] = [
        nextItems[secondIndex],
        nextItems[firstIndex],
      ];

      return nextItems;
    });

    setResult('');
  }

  function handleItemClick(itemId) {
    if (result === 'correct') {
      return;
    }

    if (!selectedId) {
      setSelectedId(itemId);
      return;
    }

    if (selectedId === itemId) {
      setSelectedId('');
      return;
    }

    swapItems(
      selectedId,
      itemId
    );

    setSelectedId('');
  }

  function checkOrder() {
    const isCorrect =
      items.every(
        (item, index) =>
          item.id ===
          JOURNEY[index].id
      );

    if (isCorrect) {
      setResult('correct');
      setSelectedId('');
      onComplete?.();
      return;
    }

    setResult('incorrect');
  }

  return (
    <section className="journey-order-activity">
      <style>{STYLES}</style>

      <header className="journey-order-heading">
        <h1>{prompt}</h1>

        <p>
          Drag the steps, or select two to
          swap them.
        </p>
      </header>

      <div className="journey-order-list">
        {items.map((item, index) => {
          const Icon = item.Icon;
          const isSelected =
            selectedId === item.id;

          return (
            <button
              type="button"
              className={[
                'journey-order-item',
                isSelected
                  ? 'is-selected'
                  : '',
                result === 'correct'
                  ? 'is-correct'
                  : '',
              ].join(' ')}
              key={item.id}
              draggable={
                result !== 'correct'
              }
              aria-pressed={isSelected}
              onClick={() =>
                handleItemClick(item.id)
              }
              onDragStart={(event) => {
                setDraggedId(item.id);

                event.dataTransfer.effectAllowed =
                  'move';

                event.dataTransfer.setData(
                  'text/plain',
                  item.id
                );
              }}
              onDragOver={(event) => {
                event.preventDefault();

                event.dataTransfer.dropEffect =
                  'move';
              }}
              onDrop={(event) => {
                event.preventDefault();

                const sourceId =
                  draggedId ||
                  event.dataTransfer.getData(
                    'text/plain'
                  );

                moveItem(
                  sourceId,
                  item.id
                );

                setDraggedId('');
              }}
              onDragEnd={() =>
                setDraggedId('')
              }
            >
              <span className="journey-order-position">
                {padNumber(index + 1)}
              </span>

              <Icon
                className="journey-order-icon"
                aria-hidden="true"
              />

              <strong>
                {item.label}
              </strong>

              {result === 'correct' ? (
                <span
                  className="journey-order-complete-mark"
                  aria-hidden="true"
                >
                  ✓
                </span>
              ) : (
                <LuGripVertical
                  className="journey-order-handle"
                  aria-hidden="true"
                />
              )}
            </button>
          );
        })}
      </div>

      <footer className="journey-order-footer">
        <div
          className={[
            'journey-order-feedback',
            result
              ? 'is-visible'
              : '',
            result === 'correct'
              ? 'is-correct'
              : 'is-incorrect',
          ].join(' ')}
          aria-live="polite"
        >
          {result === 'correct' &&
            'Correct. That is the complete member journey.'}

          {result === 'incorrect' &&
            'Not quite. Try a different order.'}
        </div>

        <button
          type="button"
          className="journey-order-check"
          onClick={checkOrder}
          disabled={result === 'correct'}
        >
          {result === 'correct'
            ? 'Complete'
            : 'Check order'}
        </button>
      </footer>
    </section>
  );
}

function WordText({
  as: Element,
  text,
  wordDelay,
}) {
  const words = text.split(' ');

  return (
    <Element aria-label={text}>
      {words.map((word, index) => (
        <React.Fragment
          key={`${word}-${index}`}
        >
          <span
            className="journey-word"
            style={{
              '--journey-word-delay':
                `${index * wordDelay}ms`,
            }}
            aria-hidden="true"
          >
            {word}
          </span>

          {index < words.length - 1
            ? ' '
            : ''}
        </React.Fragment>
      ))}
    </Element>
  );
}

function padNumber(number) {
  return String(number).padStart(
    2,
    '0'
  );
}

const STYLES = `
  .member-journey-slide {
    --lesson-font:
      Inter,
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;

    --lesson-ink: #111111;
    --lesson-muted: #818181;
    --lesson-faint: #b5b5b5;
    --lesson-rule: #dedede;

    --lesson-hero-size:
      clamp(42px, 5.4vw, 70px);

    --lesson-hero-weight: 820;
    --lesson-hero-line: 1;
    --lesson-hero-tracking: -0.045em;

    --lesson-section-size:
      clamp(28px, 3vw, 39px);

    --lesson-section-weight: 800;

    position: relative;
    width: 100%;
    min-height: 465px;
    overflow: hidden;
    box-sizing: border-box;

    color: var(--lesson-ink);
    background: #ffffff;

    font-family: var(--lesson-font);
  }

  .journey-route-scene {
    position: absolute;
    inset: 0;
  }

  /*
   * HEADLINE
   *
   * Exact same primary scale as
   * What Is Tribe.
   */

  .journey-route-title {
    position: absolute;

    top: 50%;
    left: 50%;

    width:
      min(
        920px,
        calc(100% - 64px)
      );

    text-align: center;

    transform:
      translate(-50%, -50%);

    transition:
      top
        800ms
        cubic-bezier(.18,.84,.22,1),
      transform
        800ms
        cubic-bezier(.18,.84,.22,1);
  }

  .journey-route-title h1 {
    margin: 0;

    font-size:
      var(--lesson-hero-size);

    font-weight:
      var(--lesson-hero-weight);

    line-height:
      var(--lesson-hero-line);

    letter-spacing:
      var(--lesson-hero-tracking);

    transition:
      font-size
        800ms
        cubic-bezier(.18,.84,.22,1),
      font-weight
        800ms
        cubic-bezier(.18,.84,.22,1),
      letter-spacing
        800ms
        cubic-bezier(.18,.84,.22,1);
  }

  /*
   * Once the route appears, the same
   * headline becomes a restrained
   * section heading.
   */

  .journey-route-scene.is-active
  .journey-route-title {
    top: 42px;

    transform:
      translateX(-50%);
  }

  .journey-route-scene.is-active
  .journey-route-title h1 {
    font-size:
      var(--lesson-section-size);

    font-weight:
      var(--lesson-section-weight);

    line-height: 1.04;

    letter-spacing:
      -0.038em;
  }

  /*
   * WORD REVEAL
   */

  .journey-word {
    display: inline-block;

    opacity: 0;

    transform:
      translateY(0.38em);

    animation:
      journey-word-in
      340ms
      cubic-bezier(.18,.84,.22,1)
      forwards;

    animation-delay:
      var(--journey-word-delay);
  }

  /*
   * ROUTE
   */

  .journey-route {
    position: absolute;

    top: 55%;
    left: 50%;

    width:
      min(
        1000px,
        calc(100% - 72px)
      );

    opacity: 0;

    transform:
      translate(-50%, -42%)
      scale(.985);

    transition:
      opacity 650ms ease,
      transform
        800ms
        cubic-bezier(.18,.84,.22,1);
  }

  .journey-route-scene.is-active
  .journey-route {
    opacity: 1;

    transform:
      translate(-50%, -50%)
      scale(1);
  }

  /*
   * IMPORTANT:
   *
   * line is positioned at 62px.
   *
   * icon = 42px tall
   * gap = 15px
   * dot = 10px tall
   *
   * 42 + 15 + 5 = 62
   *
   * So the CENTER of every dot lands
   * exactly on the route line.
   */

  .journey-route-line,
  .journey-route-progress {
    position: absolute;

    top: 62px;
    left: 8.333%;

    width: 83.334%;
    height: 1px;
  }

  .journey-route-line {
    background:
      var(--lesson-rule);
  }

  .journey-route-progress {
    z-index: 1;

    background:
      var(--lesson-ink);

    transform:
      scaleX(0);

    transform-origin:
      left center;

    transition:
      transform
      900ms
      cubic-bezier(.18,.84,.22,1);
  }

  .journey-route-nodes {
    position: relative;
    z-index: 2;

    display: grid;

    grid-template-columns:
      repeat(
        6,
        minmax(0, 1fr)
      );
  }

  /*
   * NODE
   *
   * Never vertically move the whole
   * node. Doing so also moves the dot
   * away from the route line.
   */

  .journey-route-node {
    display: flex;

    flex-direction: column;
    align-items: center;

    min-width: 0;

    color:
      var(--lesson-faint);

    opacity: 0.16;

    text-align: center;

    transform: none;

    transition:
      color 500ms ease,
      opacity 500ms ease;
  }

  .journey-route-node.is-revealed {
    color: #8d8d8d;

    opacity: 0.54;
  }

  .journey-route-node.is-current {
    color:
      var(--lesson-ink);

    opacity: 1;

    /*
     * Deliberately no translateY here.
     * Dot remains exactly on line.
     */
    transform: none;
  }

  .journey-route-scene.is-reviewing
  .journey-route-node {
    color:
      var(--lesson-ink);

    opacity: 1;

    transform: none;
  }

  /*
   * ICON
   */

  .journey-route-icon {
    display: flex;

    align-items: center;
    justify-content: center;

    width: 42px;
    height: 42px;

    transform-origin:
      center bottom;

    transition:
      transform
      650ms
      cubic-bezier(.18,.84,.22,1);
  }

  .journey-route-icon svg {
    width:
      clamp(25px, 2.8vw, 33px);

    height:
      clamp(25px, 2.8vw, 33px);

    stroke-width: 1.65;

    transition:
      transform
      650ms
      cubic-bezier(.18,.84,.22,1);
  }

  /*
   * Focus happens ABOVE the dot,
   * rather than translating the
   * entire node.
   */

  .journey-route-node.is-current
  .journey-route-icon {
    transform:
      translateY(-5px);
  }

  .journey-route-node.is-current
  .journey-route-icon svg {
    transform:
      scale(1.1);
  }

  /*
   * DOT
   *
   * Vertical position never changes.
   */

  .journey-route-dot {
    width: 10px;
    height: 10px;

    margin-top: 15px;

    box-sizing: border-box;

    background: #ffffff;

    border:
      1.5px solid
      #d4d4d8;

    border-radius: 50%;

    transform:
      scale(1);

    transform-origin:
      center;

    transition:
      background 450ms ease,
      border-color 450ms ease,
      transform 450ms ease;
  }

  .journey-route-node.is-revealed
  .journey-route-dot {
    background:
      #8d8d8d;

    border-color:
      #8d8d8d;
  }

  .journey-route-node.is-current
  .journey-route-dot,
  .journey-route-scene.is-reviewing
  .journey-route-dot {
    background:
      var(--lesson-ink);

    border-color:
      var(--lesson-ink);
  }

  /*
   * Scaling around the center means
   * the dot grows equally above/below
   * the line and stays centered.
   */

  .journey-route-node.is-current
  .journey-route-dot {
    transform:
      scale(1.3);
  }

  /*
   * LABEL
   */

  .journey-route-node strong {
    width: 100%;
    max-width: 130px;

    margin-top: 15px;

    padding: 0 6px;

    box-sizing: border-box;

    font-size:
      clamp(11px, 1.1vw, 14px);

    font-weight: 650;

    line-height: 1.25;

    letter-spacing:
      -0.012em;

    transform:
      translateY(0);

    transition:
      color 450ms ease,
      font-weight 450ms ease,
      transform
        650ms
        cubic-bezier(.18,.84,.22,1);
  }

  .journey-route-node.is-current
  strong {
    font-weight: 760;

    /*
     * We can move the LABEL without
     * moving the dot.
     */
    transform:
      translateY(3px);
  }

  .journey-route-scene.is-reviewing
  .journey-route-node strong {
    transform:
      translateY(0);
  }

  /*
   * ==========================================
   * ACTIVITY
   *
   * Deliberately untouched for now.
   * ==========================================
   */

  .journey-order-activity {
    width: min(690px, 100%);
    margin: auto;

    padding: 24px 20px;

    box-sizing: border-box;

    color: #111111;
    background: #ffffff;
  }

  .journey-order-heading {
    margin-bottom: 18px;

    text-align: center;
  }

  .journey-order-heading h1 {
    margin: 0;

    font-size:
      clamp(30px, 4vw, 44px);

    font-weight: 900;

    line-height: 1;

    letter-spacing:
      -0.055em;
  }

  .journey-order-heading p {
    margin: 9px 0 0;

    color: #747474;

    font-size: 13px;

    line-height: 1.4;
  }

  .journey-order-list {
    display: grid;

    gap: 6px;
  }

  .journey-order-item {
    display: grid;

    grid-template-columns:
      34px
      28px
      1fr
      22px;

    align-items: center;

    gap: 12px;

    width: 100%;

    min-height: 50px;

    padding: 7px 12px;

    border:
      1px solid
      #dddddd;

    border-radius: 8px;

    background: #ffffff;

    color: #111111;

    text-align: left;

    cursor: grab;

    transition:
      border-color 160ms ease,
      background 160ms ease,
      transform 160ms ease;
  }

  .journey-order-item:hover {
    border-color:
      #8a8a8a;
  }

  .journey-order-item:active {
    cursor: grabbing;
  }

  .journey-order-item.is-selected {
    border-color:
      #111111;

    background:
      #f5f5f5;
  }

  .journey-order-item.is-correct {
    cursor: default;
  }

  .journey-order-position {
    color:
      #777777;

    font-size:
      10px;

    font-weight:
      900;

    letter-spacing:
      0.08em;
  }

  .journey-order-item strong {
    font-size:
      14px;

    font-weight:
      750;

    letter-spacing:
      -0.02em;
  }

  .journey-order-icon {
    width: 22px;
    height: 22px;

    stroke-width: 1.8;
  }

  .journey-order-handle {
    width: 18px;
    height: 18px;

    color:
      #a0a0a0;
  }

  .journey-order-complete-mark {
    color:
      #147a52;

    font-size:
      15px;

    font-weight:
      900;

    text-align:
      center;
  }

  .journey-order-footer {
    display: flex;

    align-items: center;

    justify-content:
      space-between;

    gap: 18px;

    min-height:
      44px;

    margin-top:
      14px;
  }

  .journey-order-feedback {
    flex: 1;

    color:
      #707070;

    font-size:
      12px;

    line-height:
      1.4;

    opacity: 0;
  }

  .journey-order-feedback.is-visible {
    opacity: 1;
  }

  .journey-order-feedback.is-correct {
    color:
      #147a52;
  }

  .journey-order-feedback.is-incorrect {
    color:
      #a63c36;
  }

  .journey-order-check {
    min-width: 122px;
    height: 40px;

    padding: 0 17px;

    border:
      1px solid
      #111111;

    border-radius: 8px;

    background:
      #111111;

    color:
      #ffffff;

    font-size: 12px;

    font-weight: 800;

    cursor: pointer;
  }

  .journey-order-check:disabled {
    opacity: 0.55;

    cursor: default;
  }

  @keyframes journey-word-in {
    to {
      opacity: 1;

      transform:
        translateY(0);
    }
  }

  @media (max-width: 700px) {
    .member-journey-slide {
      min-height: 520px;
    }

    .journey-route-title {
      width:
        calc(100% - 30px);
    }

    /*
     * Matches What Is Tribe mobile
     * hero scale.
     */
    .journey-route-title h1 {
      font-size:
        clamp(
          38px,
          10.5vw,
          50px
        );
    }

    .journey-route-scene.is-active
    .journey-route-title {
      top: 34px;
    }

    .journey-route-scene.is-active
    .journey-route-title h1 {
      font-size: 27px;
    }

    .journey-route {
      top: 54%;

      width:
        calc(100% - 18px);
    }

    /*
     * Mobile alignment follows
     * exactly the same principle:
     *
     * icon height = 38
     * margin = 13
     * half dot = 4.5
     *
     * 38 + 13 + 4.5 = 55.5
     */

    .journey-route-line,
    .journey-route-progress {
      top: 55.5px;
    }

    .journey-route-icon {
      width: 34px;
      height: 38px;
    }

    .journey-route-icon svg {
      width: 23px;
      height: 23px;
    }

    .journey-route-node.is-current
    .journey-route-icon {
      transform:
        translateY(-4px);
    }

    .journey-route-dot {
      width: 9px;
      height: 9px;

      margin-top: 13px;
    }

    .journey-route-node strong {
      margin-top: 12px;

      font-size: 9px;
    }

    .journey-route-node.is-current
    strong {
      transform:
        translateY(2px);
    }

    .journey-order-activity {
      padding: 20px 0;
    }

    .journey-order-item {
      grid-template-columns:
        30px
        24px
        1fr
        20px;

      gap: 9px;

      min-height: 48px;

      padding: 6px 10px;
    }

    .journey-order-footer {
      align-items: stretch;

      flex-direction: column;

      gap: 8px;
    }

    .journey-order-check {
      width: 100%;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .journey-word {
      animation-duration:
        1ms;

      animation-delay:
        0ms;
    }

    .journey-route-title,
    .journey-route-title h1,
    .journey-route,
    .journey-route-progress,
    .journey-route-node,
    .journey-route-icon,
    .journey-route-icon svg,
    .journey-route-dot,
    .journey-route-node strong,
    .journey-order-item {
      transition-duration:
        1ms;
    }
  }
`;

MemberJourney.title =
  'The member journey';

MemberJourney.steps =
  STEPS;